import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IceCream, Mail, Lock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  /* Redirect if already logged in */
  useEffect(() => {
    if (user) {
      if (user.role === "admin" || user.role === "manager") {
        navigate("/admin");
      } else {
        navigate("/kitchen");
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const data = await login(email, password);

      toast.success(`Welcome, ${data.user.name}!`);

      if (data.user.role === "admin" || data.user.role === "manager") {
        navigate("/admin");
      } else {
        navigate("/kitchen");
      }
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* LOGO */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#38BDF8] flex items-center justify-center shadow-lg shadow-[#38BDF8]/30">
            <IceCream className="w-8 h-8 text-white" />
          </div>

          <h1 className="text-2xl font-extrabold text-[#2C3E50]">Him Cream</h1>

          <p className="text-gray-500 mt-1">Staff Login</p>
        </div>

        {/* FORM */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* EMAIL */}
            <div>
              <Label className="text-gray-700 font-medium">Email</Label>

              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@himcream.com"
                  className="pl-10 bg-gray-50 border border-gray-200 focus:border-[#38BDF8] rounded-xl h-11"
                />
              </div>
            </div>

            {/* PASSWORD */}
            <div>
              <Label className="text-gray-700 font-medium">Password</Label>

              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 bg-gray-50 border border-gray-200 focus:border-[#38BDF8] rounded-xl h-11"
                />
              </div>
            </div>

            {/* BUTTON */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#38BDF8] hover:bg-[#0EA5E9] text-white rounded-xl font-bold shadow-lg shadow-[#38BDF8]/20"
            >
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* DEMO */}
          <div className="mt-6 p-3.5 bg-stone-50 rounded-2xl border border-stone-200/80">
            <p className="text-xs text-stone-500 mb-2 font-bold uppercase tracking-wider">
              Staff & Admin Credentials:
            </p>

            <div className="space-y-1 text-xs text-stone-700">
              <p>
                Email: <span className="font-mono font-bold text-sky-700">admin@himcream.com</span>
              </p>
              <p>
                Password: <span className="font-mono font-bold text-stone-900">admin123</span>
              </p>
            </div>
          </div>
        </div>

        {/* BACK */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-gray-500 hover:text-[#38BDF8] text-sm transition"
          >
            ← Back to Menu
          </button>
        </div>
      </motion.div>
    </div>
  );
}
